import { Input, InputEvent } from '@acies/core';
import Label from './label';
import TextInputEvent from './text-input-event';
import theme from './theme';

export default class TextInput extends Input {
	public set placeholder(value: string) {
		if (this.#placeholder === value) {
			return;
		}
		assertIsString(value);
		this.#placeholder = value;
		this.#placeholderChanged = true;
		this.invalidateProperties();
	}

	public get placeholder(): string {
		return this.#placeholder;
	}

	/**
	 * The text-align CSS property sets the horizontal alignment of the inline-level content inside a block element. This means it works like vertical-align but in the horizontal direction.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/text-align} for syntax
	 */
	override set textAlign(value: 'center' | 'left' | 'right') {
		super.textAlign = value;
		this.placeholderLabel.textAlignHorizontal = value;
	}

	override get textAlign(): 'center' | 'left' | 'right' {
		return super.textAlign;
	}

	#placeholder: string;
	#placeholderChanged: boolean;
	#placeholderLabel!: Label;

	private get placeholderLabel(): Label {
		if (!this.#placeholderLabel) {
			this.#placeholderLabel = new Label();
			this.#placeholderLabel.position = 'absolute';
			this.#placeholderLabel.fontWeight = 500;
			this.#placeholderLabel.top = 7;
			this.#placeholderLabel.left = 0;
			this.#placeholderLabel.right = 0;
			this.#placeholderLabel.fill = theme.colors.text.weak;
			this.#placeholderLabel.textAlignHorizontal = this.textAlign;
		}
		return this.#placeholderLabel;
	}

	public constructor() {
		super();
		this.width = 'fill';
		this.fontFamily = 'Inter';
		this.fontWeight = 500;
		this.fontSize = 14;
		this.lineHeight = 20;
		this.fill = theme.colors.text.sub;
		this.slashedZero = true;
		this.tabularFigures = true;
		this.#placeholder = '';
		this.#placeholderChanged = false;
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#placeholderChanged) {
			this.#commitPlaceholder();
		}
		// after super, which is what writes the text into the input element: the placeholder is shown
		// or hidden by what the field ends up holding, and that is only true once the text is in it.
		// Here rather than in the text setter, so every way the text can change is covered — typed in,
		// set from code, or filtered down to nothing by a numeric field, which happens in that same
		// super call. A row emptied from code raises no input event, and that is the one this is for
		this.#updatePlaceholderLabelVisibility();
	}

	protected override enabledChanged(): void {
		super.enabledChanged();
		if (this.enabled) {
			this.placeholderLabel.fill = theme.colors.text.weak;
		} else {
			this.placeholderLabel.fill = theme.colors.text.disabled;
		}
	}

	protected override initialize(): void {
		this.appendChild(this.placeholderLabel);
		super.initialize();
		this.#addInputListeners();
	}

	#addInputListeners(): void {
		this.addEventListener(InputEvent.FOCUSED, this.#eventsListener as EventListener);
		this.addEventListener(InputEvent.BLURRED, this.#eventsListener as EventListener);
		this.addEventListener(InputEvent.CHANGED, this.#eventsListener as EventListener);
		this.addEventListener(InputEvent.ENTER, this.#eventsListener as EventListener);
	}

	#commitPlaceholder(): void {
		this.#placeholderChanged = false;
		this.placeholderLabel.text = this.#placeholder;
	}

	#dispatchTextInputEvents(event: InputEvent): void {
		if (event.type === InputEvent.BLURRED) {
			this.dispatchEvent(new TextInputEvent(TextInputEvent.BLURRED, event.text));
		} else if (event.type === InputEvent.CHANGED) {
			this.#updatePlaceholderLabelVisibility();
			this.dispatchEvent(new TextInputEvent(TextInputEvent.CHANGED, event.text));
		} else if (event.type === InputEvent.ENTER) {
			this.dispatchEvent(new TextInputEvent(TextInputEvent.ENTER, event.text));
		} else if (event.type === InputEvent.FOCUSED) {
			this.dispatchEvent(new TextInputEvent(TextInputEvent.FOCUSED, event.text));
		}
	}

	#eventsListener(event: InputEvent): void {
		event.stopPropagation();
		this.#dispatchTextInputEvents(event);
	}

	/**
	 * Shows the placeholder while the field is empty, and takes it away once it is not.
	 *
	 * Guarded against the value it already holds, because `visible` invalidates whatever it is set to: this runs on every commit, and an unguarded set would schedule the Label a commit of its own each time for a value that had not moved.
	 */
	#updatePlaceholderLabelVisibility(): void {
		const isEmpty = this.text.length === 0;
		if (this.placeholderLabel.visible !== isEmpty) {
			this.placeholderLabel.visible = isEmpty;
		}
	}
}
customElements.define('ac-text-input', TextInput);

function assertIsString(value: unknown): asserts value is string {
	if (typeof value === 'string') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type string`);
}
